import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';

type Status = 'green' | 'yellow' | 'red' | 'unknown';

@Component({
  selector: 'app-availability-gauge',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './availability-gauge.component.html',
  styleUrls: ['./availability-gauge.component.css']
})
export class AvailabilityGaugeComponent implements OnChanges {
  /** ISO string or Date of last availability update */
  @Input() updatedAt: string | Date | null = null;

  /** thresholds in days */
  @Input() greenMax = 7;
  @Input() yellowMax = 14;

  /** square size in px */
  @Input() size = 110;

  /** image config */
  @Input() imageBasePath = '/assets/gauges';
  @Input() imgGreen = 'gauge_green.png';
  @Input() imgYellow = 'gauge_yellow.png';
  @Input() imgRed = 'gauge_red.png';
  @Input() imgFallback = 'gauge_green.png';

  /** a11y/tooltip */
  @Input() showTooltip = true;

  status: Status = 'unknown';
  imageSrc = '';
  alt = 'Availability';

  ngOnChanges(_: SimpleChanges): void {
    const days = this.daysSince(this.updatedAt);
    this.status = this.daysToStatus(days, this.greenMax, this.yellowMax);
    this.imageSrc = this.pickImage(this.status);
    this.alt = this.showTooltip ? this.buildTooltip(days, this.status) : `Availability: ${this.status}`;
  }

  private daysSince(updatedAt: string | Date | null): number | null {
    if (!updatedAt) return null;
    const d = new Date(updatedAt);
    if (isNaN(+d)) return null;
    return Math.max(0, Math.floor((Date.now() - d.getTime()) / 86_400_000));
  }

  private daysToStatus(days: number | null, g: number, y: number): Status {
    if (days == null) return 'unknown';       
    if (days <= g) return 'green';
    if (days <= y) return 'yellow';
    return 'red';
  }

  private pickImage(status: Status): string {
    const base = this.imageBasePath.replace(/\/+$/, '');
    const file =
      status === 'green'  ? this.imgGreen  :
      status === 'yellow' ? this.imgYellow :
      /* red & unknown */   this.imgRed;
    return `${base}/${file}`;
  }

  onImgError(ev: Event) {
    (ev.target as HTMLImageElement).src =
      `${this.imageBasePath.replace(/\/+$/, '')}/${this.imgFallback}`;
  }

  private buildTooltip(days: number | null, status: Status) {
    if (days == null) return 'No availability on file';
    const base = `Last updated ${days} day(s) ago`;
    if (status === 'green') return `${base} (fresh)`;
    if (status === 'yellow') return `${base} (stale soon)`;
    return `${base} (outdated)`;
  }
}
